/**
 * The "Additional requirements" section on the customer screen (item #6).
 *
 * A field staff member records what the shop wants — a type from the fixed list
 * plus a note — and sees the list, marking items fulfilled or dropped. Admin
 * reads the roll-up on the Requirements screen.
 */

import { useRef, useState } from 'react';
import { Card, Select, TextInput, Button, useToast } from './ui';
import { useQuery } from '../lib/useApi';
import { api, ApiError } from '../lib/api';
import { dateShort } from '../lib/format';

export const REQUIREMENT_TYPES = [
  { value: 'visi_cooler', label: 'Visi-cooler / fridge' },
  { value: 'deep_freezer', label: 'Deep freezer' },
  { value: 'new_product', label: 'New product' },
  { value: 'new_category', label: 'New category' },
  { value: 'larger_pack', label: 'Larger pack / case' },
  { value: 'signage', label: 'Signage or branding' },
  { value: 'display_rack', label: 'Display rack' },
  { value: 'credit_increase', label: 'Credit increase' },
  { value: 'other', label: 'Other' },
];
export const REQ_LABEL: Record<string, string> = Object.fromEntries(REQUIREMENT_TYPES.map((t) => [t.value, t.label]));

interface Req {
  id: string; requirement_type: string; detail: string | null; status: string;
  created_at: string; set_by_name: string;
}

export function CustomerRequirements({ customerId }: { customerId: string }) {
  const toast = useToast();
  const { data, refetch } = useQuery<{ items: Req[] }>(
    `/api/requirements?customerId=${customerId}`, { refreshOn: ['requirements'] },
  );
  const [type, setType] = useState('visi_cooler');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const uuid = useRef<string | null>(null);

  async function add() {
    setBusy(true);
    if (!uuid.current) uuid.current = crypto.randomUUID();
    try {
      await api.post('/api/requirements', {
        customerId, requirementType: type, detail: detail.trim() || undefined, clientUuid: uuid.current,
      });
      uuid.current = null;
      setDetail('');
      toast.show('Requirement added.', 'ok');
      void refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not add', 'danger');
    } finally { setBusy(false); }
  }

  async function setStatus(id: string, status: string, msg: string) {
    try { await api.post(`/api/requirements/${id}/status`, { status }); toast.show(msg, 'ok'); void refetch(); }
    catch { toast.show('Could not update', 'danger'); }
  }

  const items = data?.items ?? [];

  return (
    <Card>
      <div className="t-semi" style={{ marginBottom: 10 }}>Additional requirements</div>

      <div className="stack-2">
        <Select
          label="What does the customer want?"
          options={REQUIREMENT_TYPES}
          value={type}
          onChange={(e) => setType((e.target as HTMLSelectElement).value)}
        />
        <TextInput
          label="Detail (optional)"
          placeholder="e.g. 300L visi-cooler for cold drinks"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
        />
        <Button busy={busy} onClick={() => void add()}>Add requirement</Button>
      </div>

      {items.length > 0 && (
        <div className="stack-2" style={{ marginTop: 14 }}>
          {items.map((r) => (
            <div key={r.id} className="row-between" style={{ borderTop: '0.5px solid var(--line)', paddingTop: 10 }}>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="t-sm t-semi">{REQ_LABEL[r.requirement_type] ?? r.requirement_type}</div>
                <div className="t-xs t-muted">
                  {r.detail ? `${r.detail} · ` : ''}{r.set_by_name} · {dateShort(r.created_at)}
                </div>
              </div>
              {r.status === 'open' || r.status === 'in_progress' ? (
                <div className="row" style={{ gap: 6 }}>
                  <Button size="sm" variant="ghost" onClick={() => void setStatus(r.id, 'dropped', 'Dropped.')}>Drop</Button>
                  <Button size="sm" onClick={() => void setStatus(r.id, 'fulfilled', 'Marked fulfilled.')}>Fulfilled</Button>
                </div>
              ) : (
                <span className="t-xs" style={{ color: r.status === 'fulfilled' ? 'var(--ok)' : 'var(--faint)' }}>
                  {r.status === 'fulfilled' ? 'Fulfilled' : 'Dropped'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
